import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Rating,
  Divider,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Skeleton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { getAuth } from 'firebase/auth';
import { 
  db,
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
  runTransaction
} from '../../firebase/firebase';

// 样式化组件
const ReviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: theme.palette.divider,
  boxShadow: 'none',
}));

const ReviewAvatar = styled('div')(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '1.25rem',
  marginRight: theme.spacing(2),
}));

const RatingBar = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main,
  },
}));

// 命理师评价组件
const FortuneTellerReviews = ({ fortuneTellerId }) => {
  const { currentUser } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState('latest');
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  });
  
  // 评价表单状态
  const [openDialog, setOpenDialog] = useState(false);
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [userBookings, setUserBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [loadingBookings, setLoadingBookings] = useState(false);

  // 使用直接Firestore操作替代Firebase Functions

  // 加载评价 - 直接从Firestore获取
  const loadReviews = async () => {
    if (!fortuneTellerId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // 构建查询
      let reviewsQuery = query(
        collection(db, 'reviews'),
        where('targetId', '==', fortuneTellerId),
        where('status', '==', 'published')
      );
      
      // 根据排序方式设置排序
      switch (sort) {
        case 'rating_high':
          reviewsQuery = query(reviewsQuery, orderBy('rating', 'desc'));
          break;
        case 'rating_low':
          reviewsQuery = query(reviewsQuery, orderBy('rating', 'asc'));
          break;
        case 'latest':
        default:
          reviewsQuery = query(reviewsQuery, orderBy('createdAt', 'desc'));
      }
      
      // 获取总数
      const totalQuery = query(
        collection(db, 'reviews'),
        where('targetId', '==', fortuneTellerId),
        where('status', '==', 'published')
      );
      const totalSnapshot = await getDocs(totalQuery);
      const total = totalSnapshot.size;
      
      // 计算分页
      const itemsPerPage = 5;
      const calculatedTotalPages = Math.ceil(total / itemsPerPage);
      setTotalPages(calculatedTotalPages || 1);
      
      // 应用分页 - Firestore没有直接的offset分页，这里使用一个简单方法
      // 注意：大数据集可能需要使用游标分页
      const pageLimit = itemsPerPage;
      reviewsQuery = query(reviewsQuery, limit(page * pageLimit));
      
      // 执行查询
      const querySnapshot = await getDocs(reviewsQuery);
      
      // 获取所有评论
      const allReviews = [];
      for (const doc of querySnapshot.docs) {
        allReviews.push({
          id: doc.id,
          ...doc.data()
        });
      }
      
      // 提取当前页数据
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const pagedReviews = allReviews.slice(startIndex, endIndex);
      
      // 获取每个评价的用户信息
      const reviewsWithUserInfo = await Promise.all(
        pagedReviews.map(async (review) => {
          try {
            // 获取用户信息
            const userDoc = await getDoc(doc(db, 'users', review.userId));
            const userData = userDoc.exists() ? userDoc.data() : null;
            
            return {
              ...review,
              userName: userData ? userData.displayName : '匿名用户',
              userAvatar: userData ? userData.photoURL : null,
              createdAt: review.createdAt ? review.createdAt.toDate().toISOString() : new Date().toISOString()
            };
          } catch (e) {
            console.error('无法获取用户信息:', e);
            return {
              ...review,
              userName: '匿名用户',
              userAvatar: null,
              createdAt: review.createdAt ? review.createdAt.toDate().toISOString() : new Date().toISOString()
            };
          }
        })
      );
      
      setReviews(reviewsWithUserInfo);
    } catch (error) {
      console.error('加载评价出错:', error);
      setError('获取评价数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 加载评价统计信息 - 直接从Firestore计算
  const loadStats = async () => {
    if (!fortuneTellerId) return;
    
    try {
      // 检查是否有缓存的统计结果
      const statsDoc = await getDoc(doc(db, 'fortuneTellerStats', fortuneTellerId));
      
      if (statsDoc.exists() && statsDoc.data().lastUpdated) {
        const statsData = statsDoc.data();
        // 如果统计结果不超过1小时，使用缓存
        const lastUpdated = statsData.lastUpdated.toDate();
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        
        if (lastUpdated > oneHourAgo) {
          console.log('使用缓存的评价统计数据');
          setStats(statsData);
          return;
        }
      }
      
      // 需要重新计算统计数据
      console.log('计算新的评价统计数据');
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('targetId', '==', fortuneTellerId),
        where('status', '==', 'published')
      );
      
      const snapshot = await getDocs(reviewsQuery);
      
      // 初始化统计数据
      const statsData = {
        totalReviews: snapshot.size,
        averageRating: 0,
        ratingDistribution: {
          1: 0, 2: 0, 3: 0, 4: 0, 5: 0
        },
        lastUpdated: serverTimestamp()
      };
      
      // 计算评分分布和平均分
      let totalRating = 0;
      snapshot.forEach(doc => {
        const rating = doc.data().rating;
        statsData.ratingDistribution[rating] = (statsData.ratingDistribution[rating] || 0) + 1;
        totalRating += rating;
      });
      
      // 计算平均分
      statsData.averageRating = statsData.totalReviews > 0 ? totalRating / statsData.totalReviews : 0;
      
      // 保存统计结果到缓存
      await setDoc(doc(db, 'fortuneTellerStats', fortuneTellerId), statsData, { merge: true });
      
      setStats(statsData);
    } catch (error) {
      console.error('加载评价统计出错:', error);
      // 不影响主评价列表加载，所以不设置全局错误
    }
  };

  // 获取用户已完成的预约
  const loadUserBookings = async () => {
    if (!currentUser) return;
    
    setLoadingBookings(true);
    
    try {
      const bookingsRef = collection(db, 'bookings');
      
      // 查询用户的已完成预约
      const bookingsQuery = query(
        bookingsRef,
        where('userId', '==', currentUser.uid),
        where('fortuneTellerId', '==', fortuneTellerId),
        where('status', '==', 'completed')
      );
      
      const bookingSnap = await getDocs(bookingsQuery);
      const bookings = [];
      
      for (const doc of bookingSnap.docs) {
        const bookingData = doc.data();
        
        // 检查是否已经评价过
        const reviewsRef = collection(db, 'reviews');
        const reviewQuery = query(
          reviewsRef,
          where('userId', '==', currentUser.uid),
          where('targetId', '==', fortuneTellerId),
          where('orderId', '==', doc.id)
        );
        
        const reviewSnap = await getDocs(reviewQuery);
        
        if (reviewSnap.empty) {
          // 未评价过的预约才添加到可评价列表
          const bookingDate = bookingData.date?.toDate?.() || 
                            (bookingData.date?.seconds ? new Date(bookingData.date.seconds * 1000) : null);
          
          bookings.push({
            id: doc.id,
            date: bookingDate ? bookingDate.toLocaleDateString() : '未知日期',
            timeSlots: bookingData.timeSlots || [],
            formattedDate: bookingData.formattedDate
          });
        }
      }
      
      setUserBookings(bookings);
      
      // 如果有预约，默认选择第一个
      if (bookings.length > 0) {
        setSelectedBookingId(bookings[0].id);
      }
    } catch (error) {
      console.error('获取预约失败：', error);
      setFormError('获取预约信息失败，请稍后再试');
    } finally {
      setLoadingBookings(false);
    }
  };

  // 首次加载和筛选条件变化时重新加载评价
  useEffect(() => {
    if (fortuneTellerId) {
      loadReviews();
      loadStats();
    }
  }, [fortuneTellerId, page, sort]);

  // 打开评价表单时加载用户预约信息
  useEffect(() => {
    if (openDialog && currentUser) {
      loadUserBookings();
    }
  }, [openDialog, currentUser, fortuneTellerId]);

  // 处理评价提交 - 直接使用Firestore事务
  const handleSubmitReview = async () => {
    if (!currentUser) {
      setFormError('请先登录后再评价');
      return;
    }
    
    if (rating < 1) {
      setFormError('请选择评分');
      return;
    }
    
    if (!content.trim()) {
      setFormError('请输入评价内容');
      return;
    }
    
    setSubmitting(true);
    setFormError('');
    
    try {
      const userId = currentUser.uid;
      
      // 检查用户是否已经评价过
      const existingReviewQuery = query(
        collection(db, 'reviews'),
        where('userId', '==', userId),
        where('targetId', '==', fortuneTellerId),
        where('targetType', '==', 'fortuneTeller'),
        limit(1)
      );
      
      const existingReviewSnapshot = await getDocs(existingReviewQuery);
      if (!existingReviewSnapshot.empty) {
        throw new Error('您已经评价过该命理师');
      }
      
      // 使用事务更新命理师评分和添加评价
      await runTransaction(db, async (transaction) => {
        // 首先完成所有读取操作
        
        // 1. 创建评价的准备工作
        const reviewRef = doc(collection(db, 'reviews'));
        
        // 获取用户信息
        const userDoc = await transaction.get(doc(db, 'users', userId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        // 2. 读取命理师信息
        const fortuneTellerRef = doc(db, 'fortuneTellers', fortuneTellerId);
        const fortuneTellerDoc = await transaction.get(fortuneTellerRef);
        
        if (!fortuneTellerDoc.exists()) {
          throw new Error('命理师不存在');
        }
        
        // 3. 读取统计缓存信息
        const statsRef = doc(db, 'fortuneTellerStats', fortuneTellerId);
        const statsDoc = await transaction.get(statsRef);
        
        // 准备评价数据
        const newReviewData = {
          userId,
          targetId: fortuneTellerId,
          targetType: 'fortuneTeller',
          rating: rating,
          content: content.trim(),
          status: 'published',
          userName: userData.displayName || '匿名用户',
          userAvatar: userData.photoURL || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // 如果选择了预约，添加预约ID
        if (selectedBookingId) {
          newReviewData.orderId = selectedBookingId;
        }
        
        // 处理命理师数据
        const fortuneTellerData = fortuneTellerDoc.data();
        const totalRating = (fortuneTellerData.totalRating || 0) + rating;
        const reviewCount = (fortuneTellerData.reviewCount || 0) + 1;
        const averageRating = totalRating / reviewCount;
        
        // 现在开始所有写入操作
        
        // 1. 创建评价
        transaction.set(reviewRef, newReviewData);
        
        // 2. 更新命理师评分
        transaction.update(fortuneTellerRef, {
          totalRating,
          reviewCount,
          averageRating,
          updatedAt: serverTimestamp()
        });
        
        // 3. 更新统计缓存
        if (statsDoc.exists()) {
          const statsData = statsDoc.data();
          const ratingDistribution = { ...statsData.ratingDistribution };
          ratingDistribution[rating] = (ratingDistribution[rating] || 0) + 1;
          
          transaction.update(statsRef, {
            totalReviews: (statsData.totalReviews || 0) + 1,
            ratingDistribution,
            averageRating: (statsData.totalReviews > 0 ? 
              (statsData.averageRating * statsData.totalReviews + rating) / (statsData.totalReviews + 1) : 
              rating),
            lastUpdated: serverTimestamp()
          });
        } else {
          // 如果统计文档不存在，创建新的
          transaction.set(statsRef, {
            totalReviews: 1,
            averageRating: rating,
            ratingDistribution: { [rating]: 1 },
            lastUpdated: serverTimestamp()
          });
        }
      });
      
      // 重置表单
      setContent('');
      setRating(5);
      setOpenDialog(false);
      
      // 刷新评价列表
      await Promise.all([loadReviews(), loadStats()]);
      
    } catch (error) {
      console.error('提交评价出错:', error);
      setFormError(error.message || '提交评价失败，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 处理页码变化
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: document.getElementById('reviews-section').offsetTop - 100,
      behavior: 'smooth'
    });
  };

  // 处理排序变化
  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  // 渲染评分分布
  const renderRatingDistribution = () => {
    const totalReviews = stats.totalReviews || 0;
    
    return (
      <Box sx={{ mb: 4 }}>
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution[rating] || 0;
          const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          
          return (
            <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ width: '40px' }}>
                {rating}星
              </Typography>
              <Box sx={{ flex: 1, mx: 1 }}>
                <RatingBar variant="determinate" value={percentage} />
              </Box>
              <Typography variant="body2" sx={{ width: '50px', textAlign: 'right' }}>
                {count}条
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // 渲染评价日期
  const formatReviewDate = (date) => {
    if (!date) return '未知日期';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : 
                    date.seconds ? new Date(date.seconds * 1000) : date;
      return format(dateObj, 'yyyy年MM月dd日', { locale: zhCN });
    } catch (error) {
      return '日期格式错误';
    }
  };

  // 渲染评价项
  const renderReviewItem = (review) => {
    return (
      <ReviewContainer key={review.id}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {review.userAvatar ? (
            <Box
              component="img"
              src={review.userAvatar}
              alt={review.userName}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                mr: 2,
                objectFit: 'cover',
              }}
            />
          ) : (
            <ReviewAvatar>
              {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
            </ReviewAvatar>
          )}
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">
              {review.userName || '匿名用户'}
            </Typography>
            <Rating
              value={review.rating}
              precision={0.5}
              readOnly
              size="small"
            />
          </Box>
          
          <Typography variant="caption" color="text.secondary">
            {formatReviewDate(review.createdAt)}
            {review.orderId && (
              <Chip 
                size="small" 
                label="已咨询" 
                color="success" 
                variant="outlined" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          {review.content}
        </Typography>
        
        {/* 商家回复 */}
        {review.replies && review.replies.length > 0 && (
          <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="subtitle2" color="primary">
              命理师回复:
            </Typography>
            <Typography variant="body2">
              {review.replies[review.replies.length - 1].content}
            </Typography>
          </Box>
        )}
      </ReviewContainer>
    );
  };

  return (
    <Box id="reviews-section" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        用户评价 ({stats.totalReviews || 0})
      </Typography>
      
      {/* 评价统计 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 0 } }}>
              <Typography variant="h3" color="primary" gutterBottom>
                {stats.averageRating.toFixed(1)}
              </Typography>
              <Rating 
                value={stats.averageRating} 
                precision={0.1} 
                readOnly 
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                共 {stats.totalReviews || 0} 条评价
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {renderRatingDistribution()}
          </Grid>
        </Grid>
      </Paper>
      
      {/* 写评价按钮 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpenDialog(true)}
        >
          写评价
        </Button>
      </Box>
      
      {/* 评价列表 */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ width: 200 }}>
            <InputLabel id="sort-select-label">排序方式</InputLabel>
            <Select
              labelId="sort-select-label"
              value={sort}
              onChange={handleSortChange}
              label="排序方式"
            >
              <MenuItem value="latest">最新评价</MenuItem>
              <MenuItem value="rating_high">评分从高到低</MenuItem>
              <MenuItem value="rating_low">评分从低到高</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {loading ? (
          <Box>
            {[1, 2].map((n) => (
              <Paper key={n} sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={120} />
                    <Skeleton variant="text" width={100} />
                  </Box>
                </Box>
                <Skeleton variant="rectangular" height={80} />
              </Paper>
            ))}
          </Box>
        ) : reviews.length > 0 ? (
          <Box>
            {reviews.map(renderReviewItem)}
            
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" gutterBottom>
              暂无评价
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              成为第一个评价此命理师的用户吧！
            </Typography>
          </Paper>
        )}
      </Box>
      
      {/* 评价弹窗 */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          评价命理师
        </DialogTitle>
        <DialogContent dividers>
          {!currentUser ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              请先登录后再评价
            </Alert>
          ) : (
            <Box>
              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}
              
              {/* 预约选择 */}
              {loadingBookings ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    正在加载预约信息...
                  </Typography>
                </Box>
              ) : userBookings.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="booking-select-label">选择预约记录</InputLabel>
                  <Select
                    labelId="booking-select-label"
                    value={selectedBookingId}
                    label="选择预约记录"
                    onChange={(e) => setSelectedBookingId(e.target.value)}
                  >
                    {userBookings.map(booking => (
                      <MenuItem key={booking.id} value={booking.id}>
                        {booking.date} ({booking.timeSlots.join(', ')})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  您尚未有已完成的预约，无法关联预约进行评价
                </Alert>
              )}
              
              {/* 评分 */}
              <Box sx={{ mb: 3 }}>
                <Typography component="legend" gutterBottom>
                  您的评分*
                </Typography>
                <Rating
                  name="fortune-teller-rating"
                  value={rating}
                  precision={1}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                  }}
                  size="large"
                />
              </Box>
              
              {/* 评价内容 */}
              <TextField
                label="评价内容*"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请分享您对命理师的咨询体验、专业水平、态度等方面的评价..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            取消
          </Button>
          <Button
            onClick={handleSubmitReview}
            color="primary"
            variant="contained"
            disabled={submitting || !currentUser || !content.trim() || rating < 1}
          >
            {submitting ? <CircularProgress size={24} /> : '提交评价'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FortuneTellerReviews;